import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Card } from 'flowbite-react';
import { FiMinusCircle, FiPlus } from 'react-icons/fi';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useStakeholder } from '../../hooks/useStakeholder';
import EditableTitle from '../../components/EditableTitle';
import ListWithActions from '../../components/common/ListWithActions';
import ValueTargetSearch, {
  type ValueTargetOption,
  TARGET_BAR_COLORS,
  labelForValueTarget,
} from '../../components/common/ValueTargetSearch';

// ─── Component ───────────────────────────────────────────────────────────────

const StakeholderDetailPage: React.FC<{ overrideStakeholderId?: string }> = ({ overrideStakeholderId }) => {
  const { stakeholderId: paramId } = useParams<{ stakeholderId: string }>();
  const stakeholderId = overrideStakeholderId || paramId;
  const { stakeholder, loading, error } = useStakeholder(stakeholderId);
  const { applyCommand, database } = useDatabase();

  // Value allocation state
  const [selectedTarget, setSelectedTarget] = useState<ValueTargetOption | null>(null);
  const [newWeight, setNewWeight] = useState(10);

  // Representative state
  const [repQuery, setRepQuery] = useState('');

  const updateName = async (newName: string) => {
    if (!stakeholderId) return;
    await applyCommand({ type: 'UpdateStakeholder', id: stakeholderId, actions: [{ type: 'SetName', name: newName }] });
  };

  const updateDescription = async (newDescription: string) => {
    if (!stakeholderId) return;
    await applyCommand({ type: 'UpdateStakeholder', id: stakeholderId, actions: [{ type: 'SetDescription', description: newDescription || null }] });
  };

  const addValueAllocation = async () => {
    if (!stakeholderId || !selectedTarget || newWeight <= 0) return;
    await applyCommand({
      type: 'SetStakeholderValue',
      allocation: {
        stakeholder_id: stakeholderId,
        target: { type: selectedTarget.type, id: selectedTarget.id },
        weight: newWeight,
      },
    });
    setSelectedTarget(null);
    setNewWeight(10);
  };

  const removeValueAllocation = async (target: { type: string; id: string }) => {
    if (!stakeholderId) return;
    await applyCommand({
      type: 'SetStakeholderValue',
      allocation: {
        stakeholder_id: stakeholderId,
        target: target as wasm.ValueTarget,
        weight: 0,
      },
    });
  };

  const addRepresentative = async (personId: string) => {
    if (!stakeholderId) return;
    await applyCommand({
      type: 'SetStakeholderRepresentative',
      representative: { stakeholder_id: stakeholderId, person_id: personId },
      enabled: true,
    });
    setRepQuery('');
  };

  const removeRepresentative = async (personId: string) => {
    if (!stakeholderId) return;
    await applyCommand({
      type: 'SetStakeholderRepresentative',
      representative: { stakeholder_id: stakeholderId, person_id: personId },
      enabled: false,
    });
  };

  // Filtered people for representative picker
  const allPeople = database ? Object.entries(database.people()) : [];
  const currentRepIds = new Set(stakeholder?.representativeIds ?? []);
  const filteredPeople = allPeople.filter(([id, p]) =>
    !currentRepIds.has(id) &&
    (repQuery === '' || p.name().toLowerCase().includes(repQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !stakeholder) {
    return (
      <div className="p-4 text-red-600">
        {error ?? 'Stakeholder not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditableTitle title={stakeholder.name} onTitleChange={updateName} />

      <Card>
        <h3 className="text-lg font-medium text-cm-text-primary mb-2">Description</h3>
        <textarea
          defaultValue={stakeholder.description ?? ''}
          onBlur={(e) => {
            if (e.target.value !== (stakeholder.description ?? '')) {
              updateDescription(e.target.value);
            }
          }}
          placeholder="Who are they and why do they matter?"
          rows={3}
          className="w-full bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
        />
      </Card>

      {/* Value Allocations */}
      <Card>
        <h3 className="text-lg font-medium text-cm-text-primary mb-3">Value Allocations</h3>
        <p className="text-sm text-cm-text-muted mb-3">
          Relative importance of tasks and outcomes from this stakeholder's perspective. Only the ratios matter, not the absolute values.
        </p>

        {(() => {
          const allocs = stakeholder.valueAllocations
            .map(alloc => ({
              target: alloc.target as unknown as { type: string; id: string },
              weight: Math.max(0, alloc.weight),
              title: labelForValueTarget(alloc.target as unknown as { type: string; id: string }, database),
            }))
            .sort((a, b) => b.weight - a.weight || a.title.localeCompare(b.title));

          const total = allocs.reduce((sum, a) => sum + a.weight, 0);

          if (allocs.length === 0) return null;

          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-4">
              {allocs.map(({ target, weight, title }) => {
                const pct = total > 0 ? (weight / total) * 100 : 0;
                return (
                  <div key={`${target.type}-${target.id}`} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${target.type === 'Task' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900' : 'bg-green-100 text-green-800 dark:bg-green-900'}`}>
                          {target.type}
                        </span>
                        <span className="text-sm text-cm-text-primary truncate">{title}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        <span className="text-xs text-cm-text-muted w-8 text-right">{weight}</span>
                        <button
                          className="text-red-400 hover:text-red-600 p-0.5 rounded hover:bg-cm-bg-hover"
                          onClick={() => removeValueAllocation(target)}
                          aria-label="Remove allocation"
                        >
                          <FiMinusCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="w-full bg-cm-border-subtle rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${TARGET_BAR_COLORS[target.type] ?? 'bg-gray-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div className="flex gap-2 mt-3 items-start">
          <div className="flex-1">
            <ValueTargetSearch database={database} selected={selectedTarget} onSelect={setSelectedTarget} />
          </div>
          <input
            type="number"
            min={1}
            max={100}
            value={newWeight}
            onChange={e => setNewWeight(parseInt(e.target.value, 10) || 1)}
            className="w-20 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2"
            placeholder="Weight"
          />
          <button
            onClick={addValueAllocation}
            disabled={!selectedTarget}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiPlus />
          </button>
        </div>
      </Card>

      {/* Representatives */}
      <Card>
        <h3 className="text-lg font-medium text-cm-text-primary mb-3">Representatives</h3>
        <p className="text-sm text-cm-text-muted mb-3">
          People authorized to speak on behalf of this stakeholder.
        </p>

        <ListWithActions
          items={(stakeholder.representativeIds).map(personId => {
            const person = database?.people()[personId];
            return {
              key: personId,
              left: (
                <span className="text-sm text-cm-text-primary">
                  {person?.name() ?? personId}
                </span>
              ),
              right: (
                <button
                  className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-cm-bg-hover"
                  onClick={() => removeRepresentative(personId)}
                  aria-label="Remove representative"
                >
                  <FiMinusCircle className="w-4 h-4" />
                </button>
              ),
            };
          })}
        />

        <div className="mt-3 relative">
          <input
            type="text"
            value={repQuery}
            onChange={e => setRepQuery(e.target.value)}
            placeholder="Search for a person..."
            className="w-full bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
          />
          {repQuery && filteredPeople.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-cm-bg-surface border border-cm-border-subtle rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredPeople.map(([id, person]) => (
                <button
                  key={id}
                  className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover"
                  onClick={() => addRepresentative(id)}
                >
                  {person.name()}
                </button>
              ))}
            </div>
          )}
          {repQuery && filteredPeople.length === 0 && (
            <div className="absolute z-10 mt-1 w-full bg-cm-bg-surface border border-cm-border-subtle rounded-lg shadow-lg px-4 py-2 text-sm text-cm-text-muted">
              No people found
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default StakeholderDetailPage;
